import { useState } from 'react'
import clsx from 'clsx'
import { AvatarImage, IconSvg } from 'components/common'
import { UserAvatar } from 'components/common/UserAvatar'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { InvitationStatus } from 'graphql/generated/scheme'
import { COUNTRY_DATA, DEFAULT_REP_SCORE } from 'utils/constants'
import { GetRepScoreIcon } from 'services/helper'
import { formatPhoneNumber } from 'utils/common'
import SimpleBar from 'simplebar-react'
import { Tooltip } from '@mui/material'
import {
	SearchContactsQueryContactTypeExtended,
	SearchOfferContactsQueryContactTypeExtended,
} from 'pages/RepClubs/Contacts/contacts.type'

type AttendeeDetailType = SearchContactsQueryContactTypeExtended | SearchOfferContactsQueryContactTypeExtended

interface AttendeeDetailModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	attendeeDetail: AttendeeDetailType | null | undefined
	onClose?: () => void
}

function getIconName(platform: string) {
	switch (platform) {
		case 'facebook': {
			return 'facebook-f'
		}
		default:
			return platform
	}
}

export function AttendeeDetailModal({ open, setOpen, attendeeDetail, onClose }: AttendeeDetailModalProps) {
	const handleClose = () => {
		onClose?.()
		setOpen(false)
	}

	if (!attendeeDetail) {
		return null
	}

	const eventContact = attendeeDetail as SearchContactsQueryContactTypeExtended
	const hasInvitedTickets = eventContact?.invitedTickets && eventContact.invitedTickets.length > 0

	return (
		<BaseModal
			open={open}
			setOpen={setOpen}
			hideHeader={true}
			customSize={clsx('w-full sm:!min-w-[350px]', {
				'sm:!max-w-6xl': attendeeDetail?.userInfo && hasInvitedTickets,
				'sm:!max-w-2xl': attendeeDetail?.userInfo && !hasInvitedTickets,
				'sm:!max-w-xs': !attendeeDetail?.userInfo,
			})}
			onClose={handleClose}
		>
			<div className="relative flex flex-col sm:flex-row w-full gap-4 sm:gap-8 text-sm overflow-hidden">
				{/* Left Column: User Info */}
				<div className="flex-1 flex flex-col gap-4 sm:gap-6 text-left min-w-0">
					<div className="flex flex-col gap-0 justify-start items-center w-full">
						<div className="relative">
							<UserAvatar
								user={attendeeDetail?.userInfo}
								className="w-24 h-24 sm:w-32 sm:h-32 mx-auto"
							/>
						</div>
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-3 sm:pt-6 w-full gap-2 sm:gap-0">
							{attendeeDetail?.userInfo?.userName ? (
								<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2 sm:gap-4">
									<div className="text-white flex flex-col justify-start gap-0.5">
										<div className="text-base sm:text-lg font-semibold">
											{attendeeDetail?.userInfo?.name}
										</div>
										<div className="text-sm text-secondary-text">
											@{attendeeDetail?.userInfo?.userName}
										</div>
									</div>
									<div className="flex gap-3 items-center justify-start sm:justify-end">
										{attendeeDetail?.userInfo?.socialMediaConnections?.map((social: any) => (
											<a
												key={social?.scheme}
												href={social.profileLink}
												target="_blank"
												referrerPolicy="no-referrer"
												className="hover:opacity-80 transition-opacity"
											>
												<IconSvg icon={getIconName(social?.scheme)} />
											</a>
										))}
									</div>
								</div>
							) : (
								<div className="flex flex-col gap-2 w-full">
									{attendeeDetail?.name && (
										<div className="text-base sm:text-lg font-semibold">{attendeeDetail?.name}</div>
									)}
									{attendeeDetail?.emailAddress && (
										<div className="flex justify-between items-center gap-2 text-sm">
											<span className="text-secondary-text">Email</span>
											<span className="font-semibold break-all sm:break-normal text-right">
												{attendeeDetail?.emailAddress}
											</span>
										</div>
									)}
									{attendeeDetail?.phoneNumber && (
										<div className="flex justify-between items-center gap-2 text-sm">
											<span className="text-secondary-text">Phone</span>
											<span className="font-semibold text-right">
												{formatPhoneNumber(attendeeDetail?.phoneNumber)}
											</span>
										</div>
									)}

									{(() => {
										const eventContact = attendeeDetail as SearchContactsQueryContactTypeExtended
										const offerContact =
											attendeeDetail as SearchOfferContactsQueryContactTypeExtended
										const invitationStatus =
											eventContact?.invitationStatus ?? offerContact?.offerInvitationStatus
										const inviter = eventContact?.inviter ?? offerContact?.offerInviter

										return (
											<>
												{invitationStatus !== InvitationStatus.None && inviter && (
													<div className="flex justify-between items-center gap-2 text-sm">
														<span className="text-secondary-text font-bold whitespace-nowrap">
															Inviter
														</span>
														<Tooltip placement="bottom" title={inviter?.userName} arrow>
															<div className="font-bold flex gap-2 items-center text-right min-w-0">
																{inviter?.name && (
																	<span className="truncate">{inviter?.name}</span>
																)}
																<div className="flex items-center gap-1 font-bold flex-shrink-0">
																	{DEFAULT_REP_SCORE}
																	<IconSvg
																		icon={GetRepScoreIcon(DEFAULT_REP_SCORE)}
																	/>
																</div>
															</div>
														</Tooltip>
													</div>
												)}
											</>
										)
									})()}
								</div>
							)}
						</div>
					</div>
					{attendeeDetail?.userInfo?.userName && (
						<div className="flex flex-col gap-2 sm:gap-2.5">
							<div className="flex justify-between items-center text-sm">
								<span className="text-secondary-text">Rep Score</span>
								<div className="flex items-center gap-1 font-bold">
									{DEFAULT_REP_SCORE}
									<IconSvg icon={GetRepScoreIcon(DEFAULT_REP_SCORE)} />
								</div>
							</div>
							<div className="flex justify-between items-start sm:items-center gap-2 text-sm">
								<span className="text-secondary-text whitespace-nowrap">Location</span>
								<div className="font-bold text-right sm:text-right min-w-0 flex-1">
									{attendeeDetail?.userInfo?.address?.city && (
										<>{attendeeDetail?.userInfo?.address?.city}, </>
									)}
									{attendeeDetail?.userInfo?.address?.countryCode && (
										<>
											{
												COUNTRY_DATA.find(
													x => x.value === attendeeDetail?.userInfo?.address?.countryCode
												)?.name
											}
										</>
									)}
								</div>
							</div>

							{(() => {
								const eventContact = attendeeDetail as SearchContactsQueryContactTypeExtended
								const offerContact = attendeeDetail as SearchOfferContactsQueryContactTypeExtended
								const invitationStatus =
									eventContact?.invitationStatus ?? offerContact?.offerInvitationStatus
								const inviter = eventContact?.inviter ?? offerContact?.offerInviter

								return (
									<>
										{invitationStatus !== InvitationStatus.None && (
											<div className="flex justify-between items-center gap-2 text-sm">
												<span className="text-secondary-text font-bold whitespace-nowrap">
													Inviter
												</span>
												{inviter ? (
													<Tooltip placement="bottom" title={inviter?.userName} arrow>
														<div className="font-bold flex gap-2 items-center text-right min-w-0">
															{inviter?.name && (
																<span className="truncate">{inviter?.name}</span>
															)}
															<div className="flex items-center gap-1 font-bold flex-shrink-0">
																{DEFAULT_REP_SCORE}
																<IconSvg icon={GetRepScoreIcon(DEFAULT_REP_SCORE)} />
															</div>
														</div>
													</Tooltip>
												) : (
													<div className="font-bold text-right">Joined Directly</div>
												)}
											</div>
										)}
									</>
								)
							})()}
						</div>
					)}
				</div>

				{/* Middle Column: Invited Tickets */}
				{attendeeDetail?.userInfo && hasInvitedTickets && (
					<div className="flex-1 border-t-2 sm:border-t-0 sm:border-l-2 border-translucent-plus-border pt-4 sm:pt-0 mt-4 sm:mt-0 shrink-0">
						<h2 className="text-center text-white text-xl sm:text-2xl font-bold font-['SF Pro Display'] leading-tight mt-0 sm:mt-3 mb-3 sm:mb-0">
							Invited Tickets
						</h2>
						<div className="mt-6">
							<SimpleBar className="max-h-[250px] sm:max-h-[350px]" autoHide={false}>
								<div className="flex flex-col gap-4 sm:gap-6 sm:pl-6">
									{eventContact?.invitedTickets?.map((ticket, index) => (
										<div
											key={index}
											className="flex justify-between gap-2 p-3 border border-solid border-translucent-plus-border rounded-lg bg-secondary-surface/[0.2] backdrop-blur-lg"
										>
											{ticket.giftEventTicketName && (
												<div className="flex flex-col gap-1">
													<span className="text-xs text-secondary-text text-left">
														Gift Ticket
													</span>
													<span className="font-semibold text-sm">
														{ticket.giftEventTicketName}
														<span className="text-secondary-text ml-1">
															(x{ticket.giftEventTicketAmount})
														</span>
													</span>
												</div>
											)}
											{ticket.privateEventTicketName && (
												<div className="flex flex-col gap-1">
													<span className="text-xs text-secondary-text text-left">
														Private Ticket
													</span>
													<span className="font-semibold text-sm">
														{ticket.privateEventTicketName}
														<span className="text-secondary-text ml-1">
															(x{ticket.privateEventTicketAmount})
														</span>
													</span>
												</div>
											)}
											{ticket.invitedAt && (
												<div className="flex flex-col gap-1">
													<span className="text-xs text-secondary-text text-right">
														Invited
													</span>
													<span className="font-semibold text-sm">
														{new Date(ticket.invitedAt).toLocaleDateString()}
													</span>
												</div>
											)}
										</div>
									))}
								</div>
							</SimpleBar>
						</div>
					</div>
				)}

				{/* Right Column: Previous Events */}
				{attendeeDetail?.userInfo && (
					<div className="flex-1 border-t-2 sm:border-t-0 sm:border-l-2 border-translucent-plus-border pt-4 sm:pt-0 mt-4 sm:mt-0 shrink-0">
						<h2 className="text-center text-white text-xl sm:text-2xl font-bold font-['SF Pro Display'] leading-tight mt-0 sm:mt-3 mb-3 sm:mb-0">
							Previous Events
						</h2>
						<div className="mt-6">
							<SimpleBar className="max-h-[250px] sm:max-h-[350px]" autoHide={false}>
								<div className="flex flex-col gap-4 sm:gap-6 sm:px-6">
									{attendeeDetail?.events?.nodes?.map((item, index) => (
										<div
											key={index}
											className="flex items-center gap-2 sm:gap-3 p-2 border border-solid border-translucent-plus-border rounded-lg bg-secondary-surface/[0.2] backdrop-blur-lg text-sm sm:text-base"
										>
											<AvatarImage
												src={item?.avatarUrl}
												className="w-8 h-8 sm:w-9 sm:h-9 border border-solid border-translucent-plus-border rounded-lg flex-shrink-0"
											/>
											<div className="text-sm line-clamp-2 text-left" title={item?.displayName}>
												{item?.displayName}
											</div>
										</div>
									))}
								</div>
							</SimpleBar>
						</div>
					</div>
				)}
			</div>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface AttendeeDetailModalManagedProps {
	attendeeDetail: AttendeeDetailType | null | undefined
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'attendee-detail': AttendeeDetailModalManagedProps
	}
}

export function ManagedAttendeeDetailModal({
	onClose,
	...rest
}: AttendeeDetailModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<AttendeeDetailModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onClose={handleClose}
			{...rest}
		/>
	)
}
